// ============================================================
// 发布渲染器 renderHtml
// ============================================================

const CANVAS_W = 1600
const CANVAS_H = 900

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function escapeAttr(str) {
  return escapeHtml(str)
}

/**
 * 生成水印 HTML（注入导出产物的 #wm 固定层；文字留空返回空串 = 不加水印）。
 * @param {string|object} wm 字符串等同于 { text }；对象字段：
 *   text    水印文字（必填，空串不加水印）
 *   mode    位置模式：tile 全屏平铺（默认）/ corner 指定角落 / top 顶部横条
 *   corners 角落模式生效，勾选的角：tl/tr/bl/br 任意组合（如只选 br = 仅右下角），缺省四角
 *   density 平铺密度：sparse 疏 / medium 中（默认，等同原 8x6 效果）/ dense 密
 *   color   水印颜色：#hex / 颜色名 / rgb()，缺省 #fff（即原效果）；非法值回退 #fff
 */
const WM_COLOR_RE = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{2,32}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\))$/
export function buildWatermarkHtml(wm) {
  const opt = typeof wm === 'string' ? { text: wm } : (wm || {})
  const text = opt.text || ''
  if (!text) return ''
  const t = escapeHtml(text)
  const safeColor = WM_COLOR_RE.test(String(opt.color || '').trim()) ? String(opt.color).trim() : '#fff'
  const mk = (style) => `<span style="position:absolute;color:${safeColor};${style}">${t}</span>`
  if (opt.mode === 'top') {
    return mk('left:50%;top:14px;transform:translateX(-50%);font-size:20px;opacity:.4;white-space:nowrap;')
  }
  if (opt.mode === 'corner') {
    const pos = {
      tl: 'left:14px;top:14px;',
      tr: 'right:14px;top:14px;',
      bl: 'left:14px;bottom:14px;',
      br: 'right:14px;bottom:14px;'
    }
    const picked = (Array.isArray(opt.corners) && opt.corners.length) ? opt.corners.filter((c) => pos[c]) : []
    const corners = picked.length ? picked : ['tl', 'tr', 'bl', 'br']
    return corners.map((c) => mk(pos[c] + 'font-size:20px;opacity:.35;white-space:nowrap;')).join('')
  }
  // tile 全屏平铺
  const dens = {
    sparse: { r: 5, c: 4, x: 25, y: 20 },
    medium: { r: 8, c: 6, x: 18, y: 13 },
    dense: { r: 11, c: 8, x: 13, y: 9.5 }
  }
  const d = dens[opt.density] || dens.medium
  let h = ''
  for (let r = 0; r < d.r; r++) {
    for (let c = 0; c < d.c; c++) {
      h += mk(`left:${c * d.x}%;top:${r * d.y}%;transform:rotate(-24deg);opacity:.14;font-size:22px;white-space:nowrap;`)
    }
  }
  return h
}

// 渲染单页的所有元素
function renderElements(page, theme) {
  return (page.elements || []).map((e) => {
    const base = `left:${e.x}px;top:${e.y}px;width:${e.w}px;height:${e.h}px;`
    if (e.type === 'text') {
      const s = e.style || {}
      const css = base
      // 垂直对齐：PPT 文本框默认顶部对齐；middle/bottom 用 flex
      let vcss = 'display:block;'
      if (s.verticalAlign === 'middle') vcss = 'display:flex;align-items:center;'
      else if (s.verticalAlign === 'bottom') vcss = 'display:flex;align-items:flex-end;'
      // 逐段渲染：行距 / 段前距 / 缩进 / 对齐（转换器输出的段落级属性，还原 PPT 段落布局）
      const paragraphs = Array.isArray(s.paragraphs) ? s.paragraphs : []
      const lines = String((e.data && e.data.text) || '').split('\n')
      const inner = lines.map((t, i) => {
        const p = paragraphs[i] || {}
        const lh = p.lh || 1.2
        const before = p.before || 0
        const palign = p.align || s.align || 'left'
        let ps = `text-align:${palign};font-size:${s.fontSize || 28}px;color:${s.color || theme.text};line-height:${lh};`
        if (s.bold) ps += 'font-weight:bold;'
        if (before > 0) ps += `margin-top:${before}px;`
        if (p.marL > 0) ps += `padding-left:${p.marL}px;`
        if (p.marR > 0) ps += `padding-right:${p.marR}px;`
        if (p.indent) ps += `text-indent:${p.indent}px;`
        return `<div style="${ps}">${escapeHtml(t)}</div>`
      }).join('')
      return `<div class="el el-text" style="${css}${vcss}">${inner}</div>`
    }
    if (e.type === 'image') {
      const s = e.style || {}
      // 仅"覆盖整画布的背景图"按画布 contain（完整显示）；普通超画布图片保持原尺寸
      // （若缩小，会与叠加其上的文字/答案等独立元素错位——如内容图 + 红字答案）
      let ix = e.x, iy = e.y, iw = e.w, ih = e.h
      if (ix <= 0 && iy <= 0 && iw >= CANVAS_W - 10 && ih >= CANVAS_H - 10) {
        const k = Math.min(CANVAS_W / iw, CANVAS_H / ih)
        ix = Math.round(ix * k * 10) / 10; iy = Math.round(iy * k * 10) / 10
        iw = Math.round(iw * k * 10) / 10; ih = Math.round(ih * k * 10) / 10
      }
      const css = `left:${ix}px;top:${iy}px;width:${iw}px;height:${ih}px;overflow:hidden;` +
        (s.radius ? `border-radius:${s.radius}px;` : '') +
        (s.shadow ? 'box-shadow:0 10px 34px rgba(0,0,0,.16);' : '')
      return `<div class="el el-image" style="${css}"><img src="${escapeAttr((e.data && e.data.src) || '')}" alt="" loading="lazy"></div>`
    }
    if (e.type === 'video') {
      const s = e.style || {}
      const css = base + 'overflow:hidden;background:#000;' +
        (s.radius ? `border-radius:${s.radius}px;` : '')
      const auto = (e.data && e.data.autoPlay) ? 'autoplay muted loop' : 'controls'
      return `<div class="el el-video" style="${css}"><video src="${escapeAttr((e.data && e.data.src) || '')}" ${auto} preload="metadata" style="width:100%;height:100%;object-fit:contain;display:block;"></video></div>`
    }
    if (e.type === 'line') {
      const s = e.style || {}
      const lw = s.lineWidth || 3
      const opc = (s.opacity != null) ? `opacity:${s.opacity};` : ''
      // 优先：转换器已给出端点（含 flipH/flipV 修正）→ SVG line 精确绘制（避免旋转细线的包围盒/端点问题）
      if (s.x1 != null && s.y1 != null && s.x2 != null && s.y2 != null) {
        const lc = s.bg || '#999999'
        const box = `left:${e.x}px;top:${e.y}px;width:${e.w}px;height:${e.h}px;overflow:visible;`
        let lineHtml = `<svg class="el el-line" style="${box}${opc}">` +
          `<line x1="${s.x1}" y1="${s.y1}" x2="${s.x2}" y2="${s.y2}" stroke="${lc}" stroke-width="${lw}" stroke-linecap="round"></line></svg>`
        if (s.arrow) {
          const tipx = Math.round((e.x + s.x2) * 10) / 10, tipy = Math.round((e.y + s.y2) * 10) / 10
          const as = Math.max(10, lw * 4)
          const dx = s.x2 - s.x1, dy = s.y2 - s.y1
          const len = Math.sqrt(dx * dx + dy * dy) || 1
          const ux = dx / len, uy = dy / len
          const px1 = Math.round((tipx - as * ux + as * 0.55 * -uy) * 10) / 10
          const py1 = Math.round((tipy - as * uy + as * 0.55 * ux) * 10) / 10
          const px2 = Math.round((tipx - as * ux - as * 0.55 * -uy) * 10) / 10
          const py2 = Math.round((tipy - as * uy - as * 0.55 * ux) * 10) / 10
          lineHtml += `<svg class="el" style="${box}${opc}">` +
            `<path d="M${tipx} ${tipy} L${px1} ${py1} L${px2} ${py2} Z" fill="${lc}"></path></svg>`
        }
        return lineHtml
      }
      // 无端点（旧数据）回退：按包围盒对角线旋转画细线
      const len = Math.sqrt(e.w * e.w + e.h * e.h) || 1
      const ang = (s.angle != null) ? s.angle : Math.round(Math.atan2(e.h, e.w) * 180 / Math.PI)
      const css = `left:${e.x}px;top:${e.y}px;width:${len}px;height:${lw}px;background:${s.bg || '#999999'};` +
        `transform-origin:left center;transform:rotate(${ang}deg);${opc}`
      let arrowHtml = ''
      if (s.arrow) {
        const rad = ang * Math.PI / 180
        const tx = Math.round(e.x + len * Math.cos(rad))
        const ty = Math.round(e.y + len * Math.sin(rad))
        const as = Math.max(10, lw * 3)
        const half = Math.round(as * 0.5)
        arrowHtml = `<div class="el" style="left:${tx}px;top:${ty - half}px;width:0;height:0;` +
          `border-left:${as}px solid ${s.bg || '#999999'};` +
          `border-top:${half}px solid transparent;border-bottom:${half}px solid transparent;` +
          `transform-origin:0 50%;transform:rotate(${ang}deg);"></div>`
      }
      return `<div class="el el-line" style="${css}"></div>` + arrowHtml
    }
    if (e.type === 'circle') {
      const s = e.style || {}
      const css = base + `background:${s.bg || theme.primary};border-radius:50%;` +
        (s.opacity != null ? `opacity:${s.opacity};` : '')
      return `<div class="el el-circle" style="${css}"></div>`
    }
    // shape：自定义几何路径（SVG path 原样还原）；否则纯色块
    const s = e.style || {}
    if (e.path) {
      const fill = (s.bg && s.bg !== 'none') ? s.bg : 'none'
      let stroke = ''
      if (s.borderColor) stroke = ` stroke="${s.borderColor}" stroke-width="${s.borderWidth || 1}"`
      return `<svg class="el el-shape" style="${base}overflow:visible;${s.opacity != null ? `opacity:${s.opacity};` : ''}">` +
        `<path d="${escapeAttr(e.path)}" fill="${fill}"${stroke} stroke-linejoin="round"></path></svg>`
    }
    const bg = (s.bg && s.bg !== 'none') ? s.bg : theme.primary
    const css = base + `background:${bg};` +
      (s.radius ? `border-radius:${s.radius}px;` : '') +
      (s.opacity != null ? `opacity:${s.opacity};` : '')
    return `<div class="el el-shape" style="${css}"></div>`
  }).join('\n      ')
}

function renderSlide(page, theme, index) {
  const bg = (page.bg || theme.bg)
  return `<section class="slide" data-index="${index}" style="background:${bg}">
      ${renderElements(page, theme)}
    </section>`
}

/**
 * 渲染完整 HTML PPT
 * @param {object} ppt  PPT JSON（见 frontend/README-接入说明.md 数据结构）
 * @param {object} options
 * @param {string} options.watermark  水印文字（留空不加水印）
 * @param {boolean} options.antiCopy  是否开启防盗（禁右键/选中/拖图），默认 true
 * @param {boolean} options.showNav   是否显示底部翻页栏，默认 true
 */
export function renderHtml(ppt, options = {}) {
  const { watermark = '', antiCopy = true, showNav = true } = options
  const theme = ppt.theme || { bg: '#ffffff', primary: '#2f9bff', text: '#1f2430', title: '#0f2740', fontFamily: 'sans-serif' }
  const pages = ppt.pages || []
  const title = ppt.title || 'HTML PPT'
  const wmHtml = buildWatermarkHtml(watermark)

  const slides = pages.map((p, i) => renderSlide(p, theme, i)).join('\n    ')

  const copyJs = antiCopy
    ? `document.addEventListener('contextmenu',function(e){e.preventDefault();});document.addEventListener('selectstart',function(e){e.preventDefault();});document.addEventListener('copy',function(e){e.preventDefault();});var imgs=document.querySelectorAll('img');for(var i=0;i<imgs.length;i++){imgs[i].addEventListener('dragstart',function(e){e.preventDefault();});}`
    : ''

  const navHtml = showNav
    ? `<div id="nav"><button id="prevBtn">‹</button><span id="pageInfo">1 / ${pages.length}</span><button id="nextBtn">›</button></div>`
    : ''

  const js = `
  (function(){
    var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
    var idx = 0;
    function show(i){
      idx = (i + slides.length) % slides.length;
      slides.forEach(function(s, n){ s.classList.toggle('active', n === idx); });
      var pi = document.getElementById('pageInfo');
      if (pi) pi.textContent = (idx + 1) + ' / ' + slides.length;
    }
    function next(){ show(idx + 1); }
    function prev(){ show(idx - 1); }

    // 自适应缩放：画布固定 1600x900，整体等比缩放到窗口
    var viewer = document.getElementById('viewer');
    function fit(){
      var s = Math.min(window.innerWidth / ${CANVAS_W}, window.innerHeight / ${CANVAS_H});
      var tx = (window.innerWidth - ${CANVAS_W} * s) / 2;
      var ty = (window.innerHeight - ${CANVAS_H} * s) / 2;
      viewer.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + s + ')';
    }
    window.addEventListener('resize', fit);
    fit();

    show(0); // 初始显示第一页（原缺失，导致所有页 display:none）

    // 翻页交互（Enter/NumpadEnter 兼容发回车键值的翻页笔）
    document.addEventListener('keydown', function(e){
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'NumpadEnter') next();
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') prev();
    });
    var nb = document.getElementById('nextBtn');
    var pb = document.getElementById('prevBtn');
    if (nb) nb.addEventListener('click', next);
    if (pb) pb.addEventListener('click', prev);
    var tx = 0;
    document.addEventListener('touchstart', function(e){ tx = e.touches[0].clientX; }, {passive:true});
    document.addEventListener('touchend', function(e){
      var dx = e.changedTouches[0].clientX - tx;
      if (Math.abs(dx) > 50) { dx > 0 ? prev() : next(); }
    }, {passive:true});

    ${copyJs}
  })();
  `

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
<meta name="robots" content="noindex">
<title>${escapeHtml(title)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { height: 100%; }
  body {
    overflow: hidden;
    background: #141824;
    font-family: ${theme.fontFamily || 'sans-serif'};
    -webkit-touch-callout: none;
    -webkit-user-select: none; user-select: none;
  }
  #stage { position: fixed; inset: 0; overflow: hidden; }
  #viewer {
    position: absolute; left: 0; top: 0;
    width: ${CANVAS_W}px; height: ${CANVAS_H}px;
    transform-origin: 0 0;
    box-shadow: 0 20px 60px rgba(0,0,0,.45);
  }
  .slide { position: absolute; inset: 0; display: none; overflow: hidden; }
  .slide.active { display: block; animation: fadeIn .35s ease; }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .el { position: absolute; }
  .el-text { white-space: normal; }
  .el-image img { width: 100%; height: 100%; object-fit: fill; display: block; pointer-events: none; }
  #nav {
    position: fixed; left: 50%; bottom: 22px; transform: translateX(-50%);
    display: flex; align-items: center; gap: 14px; z-index: 20;
  }
  #nav button {
    width: 46px; height: 46px; border-radius: 50%; border: none; cursor: pointer;
    background: rgba(255,255,255,.14); color: #fff; font-size: 22px; line-height: 1;
  }
  #nav button:hover { background: rgba(255,255,255,.28); }
  #pageInfo { color: rgba(255,255,255,.8); font-size: 14px; min-width: 70px; text-align: center; }
  #wm { position: fixed; inset: 0; pointer-events: none; z-index: 30; overflow: hidden; color: #fff; }
</style>
</head>
<body>
  <div id="stage">
    <div id="viewer">
    ${slides}
    </div>
  </div>
  ${navHtml}
  <div id="wm">${wmHtml}</div>
  <script>${js}</script>
</body>
</html>`
}

// ============================================================
// 栅格化渲染
// 用途：
//  - 导入 .pptx 转换后，pages_json 每页为 {id, name, img}（OSS 图片直链）
//  - renderRasterHtml  ：发布用，生成自包含 HTML（图 + 翻页 + 全屏 + 防盗）
//  - renderRasterInto  ：预览用，插入当前 DOM（列表/编辑页实时预览）
// 旧 renderHtml（逐元素）保留用于兼容历史数据，新项目一律走栅格化。
// ============================================================

function rasterEscapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// 栅格化翻页内核（预览/发布共用），返回控制器 { show, next, prev, total, current }
// 支持动画关键帧序列页（DOM 元素带 data-frames=[初始帧,步骤1,...]）：
//   next 先推进本页帧，播完才翻页；prev 先回退帧，回到初始帧才回退页面
function rasterNavCore(slides, infoEl, opts = {}) {
  let idx = 0
  const total = slides.length
  function frameCount(s) {
    if (!s.dataset || !s.dataset.frames) return 1
    try { return JSON.parse(s.dataset.frames).length } catch (e) { return 1 }
  }
  function frameIdx(s) { return Number(s.dataset && s.dataset.step) || 0 }
  function setFrame(s, i) {
    if (!s.dataset || !s.dataset.frames) return
    let arr
    try { arr = JSON.parse(s.dataset.frames) } catch (e) { return }
    const n = Math.max(0, Math.min(arr.length - 1, i))
    s.dataset.step = String(n)
    const img = s.querySelector('img')
    if (img) img.src = arr[n]
  }
  function updateInfo() {
    if (!infoEl) return
    const s = slides[idx]
    const fc = frameCount(s)
    infoEl.textContent = fc > 1
      ? (idx + 1) + ' / ' + total + ' · 动画步骤 ' + frameIdx(s) + ' / ' + (fc - 1)
      : (idx + 1) + ' / ' + total
  }
  function show(i) {
    idx = (i + total) % total
    slides.forEach((s, n) => s.classList.toggle('active', n === idx))
    updateInfo()
    if (opts.onChange) opts.onChange(idx)
  }
  function next() {
    const s = slides[idx]
    if (frameCount(s) > 1 && frameIdx(s) < frameCount(s) - 1) { setFrame(s, frameIdx(s) + 1); updateInfo(); return }
    show(idx + 1)
  }
  function prev() {
    const s = slides[idx]
    if (frameCount(s) > 1 && frameIdx(s) > 0) { setFrame(s, frameIdx(s) - 1); updateInfo(); return }
    show(idx - 1)
  }
  if (opts.nextBtn) opts.nextBtn.addEventListener('click', next)
  if (opts.prevBtn) opts.prevBtn.addEventListener('click', prev)
  if (opts.fullBtn) {
    opts.fullBtn.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen()
      else document.documentElement.requestFullscreen()
    })
  }
  document.addEventListener('keydown', (e) => {
    if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return // 输入框内按键不翻页，也避免 preventDefault 挡掉空格输入
    if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'NumpadEnter') { next(); e.preventDefault() }
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { prev(); e.preventDefault() }
    if (opts.fullBtn && (e.key === 'f' || e.key === 'F')) opts.fullBtn.click()
  })
  let tx = 0
  document.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX }, { passive: true })
  document.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - tx
    if (Math.abs(dx) > 50) (dx > 0 ? prev() : next())
  }, { passive: true })
  show(0)
  return { show, next, prev, total, get current() { return idx } }
}

/**
 * 栅格化发布：生成自包含 HTML 字符串（每页整页渲染图 + 翻页/全屏/防盗）
 * 页型支持（pages_json 每页 type 字段，缺省 image）：
 *   - image    整页图
 *   - sequence 动画关键帧序列（frames=[初始帧,步骤1,...]，点击「下一页」逐帧播放，播完再翻页）
 *   - hybrid   整页图 + 内嵌视频原位叠加（videoBox 为画布坐标 pt，相对 pageW/pageH 定位）
 *   - video    整页图（poster）+ 全页视频播放（动画页界面上传视频后的呈现）
 * @param {object} ppt  项目对象（含 title、pages_json 解析后数组 [{id,name,img,type,frames,videoUrl,videoBox}]、pageW、pageH）
 * @param {object} options { watermark, antiCopy, showNav, showFull }
 */
export function renderRasterHtml(ppt, options = {}) {
  const { watermark = '', antiCopy = true, showNav = true, showFull = true, protected: isProtected = false, assetKeys = [] } = options
  const pages = ppt.pages || []
  const title = ppt.title || 'HTML PPT'
  const pageW = Number(ppt.pageW) || options.pageW || 960
  const pageH = Number(ppt.pageH) || options.pageH || 540
  const wmHtml = buildWatermarkHtml(watermark)

  const slides = pages.map((p) => {
    let extra = ''
    const hasFrames = Array.isArray(p.frames) && p.frames.length >= 2
    let s = `<div class="slide"`
    if (hasFrames) {
      // 动画关键帧序列：frames JSON 存于 data-frames（单引号包裹，内部转义）
      // 视频页(hybrid)可同时携带帧序列：帧逐步播放 + 视频原位叠加，互不影响
      const framesJson = JSON.stringify(p.frames).replace(/'/g, '&#39;')
      extra = ` data-frames='${framesJson}' data-step="0"`
      // 录制音效（短音效/配乐）：逐段跟帧播放（每帧时刻 + 末段终点），
      // 长音频页走下方 audioMedia 播控，不引用录制段（避免截断/双重播放）
      if (p.audio && Array.isArray(p.frameT) && p.frameT.length === p.frames.length) {
        const audioUrl = rasterEscapeHtml(p.audio).replace(/'/g, '&#39;')
        extra += ` data-audio='${audioUrl}' data-framet='${JSON.stringify(p.frameT)}'`
        if (p.audioT1 != null && Number(p.audioT1) > 0) {
          extra += ` data-t1='${Number(p.audioT1)}'`
        }
      }
    }
    // 音频媒体对象（原 PPT 带小喇叭+进度条播控）：全量数组，每个音频一个播控；
    // 主音频（第一个）带自动播放范围，行为与原 PPT 一致
    const audioList = Array.isArray(p.audios) && p.audios.length
      ? p.audios
      : (p.audioMedia ? [{ url: p.audioMedia, box: p.audioBox, durMs: p.audioDurMs || 0 }] : [])
    if (audioList.length) {
      const audioJson = audioList.map((au, ai) => {
        const b = (au.box && Number(au.box.w) > 0) ? au.box : (au.box || null)
        const ax = b ? (Number(b.x) || 0) / pageW * 100 : 10
        const ay = b ? (Number(b.y) || 0) / pageH * 100 : 80
        const aw = b ? Math.max(14, (Number(b.w) || 100) / pageW * 100) : 30
        return { url: au.url, abox: { x: ax, y: ay, w: aw }, durMs: au.durMs || 0, primary: ai === 0 }
      })
      extra += ` data-audios='${JSON.stringify(audioJson).replace(/'/g, '&#39;')}'`
      if (p.audioStart != null && p.audioEnd != null && Number(p.audioEnd) > Number(p.audioStart)) {
        extra += ` data-astart='${Number(p.audioStart)}' data-aend='${Number(p.audioEnd)}'`
      }
    }
    // 受保护导出：img/video 只留 data-src/data-poster 占位（引导脚本鉴权后回填真 URL）
    const imgSrcAttr = isProtected ? 'data-src' : 'src'
    s += extra + `><img ${imgSrcAttr}="${rasterEscapeHtml((hasFrames && p.frames[0]) || p.img || '')}" alt="">`
    // 视频对象：全量数组（兼容旧单字段）；整页视频(type=video)为用户补录
    const videoSrcAttr = isProtected ? 'data-src' : 'src'
    const posterAttr = isProtected ? 'data-poster' : 'poster'
    const videoList = Array.isArray(p.videos) && p.videos.length
      ? p.videos
      : (p.videoUrl ? [{ url: p.videoUrl, box: p.videoBox }] : [])
    videoList.forEach((v) => {
      if (v.url && v.box && Number(v.box.w) > 0) {
        const x = (Number(v.box.x) || 0) / pageW * 100
        const y = (Number(v.box.y) || 0) / pageH * 100
        const w = (Number(v.box.w) || 100) / pageW * 100
        const h = (Number(v.box.h) || 100) / pageH * 100
        s += `<video ${videoSrcAttr}="${rasterEscapeHtml(v.url)}" controls preload="metadata" style="position:absolute;left:${x}%;top:${y}%;width:${w}%;height:${h}%;object-fit:contain;background:#000;border-radius:2px;"></video>`
      } else if (v.url && p.type === 'video') {
        s += `<video ${videoSrcAttr}="${rasterEscapeHtml(v.url)}" controls preload="metadata" ${posterAttr}="${rasterEscapeHtml(p.img || '')}" style="position:absolute;left:0;top:0;width:100%;height:100%;object-fit:contain;background:#000;"></video>`
      }
    })
    return s + '</div>'
  }).join('\n    ')

  const navHtml = showNav ? `<div id="nav"><button id="prevBtn">‹</button><span id="pageInfo">1 / ${pages.length}</span><button id="nextBtn">›</button>${showFull ? '<button id="fullBtn">⛶</button>' : ''}</div>` : ''

  const copyJs = antiCopy
    ? `document.addEventListener('contextmenu',function(e){e.preventDefault();});document.addEventListener('selectstart',function(e){e.preventDefault();});document.addEventListener('copy',function(e){e.preventDefault();});var imgs=document.querySelectorAll('img');for(var i=0;i<imgs.length;i++){imgs[i].addEventListener('dragstart',function(e){e.preventDefault();});}`
    : ''

  const js = `
  (function(){
    var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
    var info = document.getElementById('pageInfo');
    var idx = 0;
    function frameCount(s){ var f = s.getAttribute('data-frames'); if (!f) return 1; try { return JSON.parse(f).length; } catch(e){ return 1; } }
    function frameIdx(s){ return parseInt(s.getAttribute('data-step') || '0', 10); }
    function setFrame(s, i){
      var f = s.getAttribute('data-frames'); if (!f) return;
      var arr; try { arr = JSON.parse(f); } catch(e){ return; }
      var n = Math.max(0, Math.min(arr.length - 1, i));
      s.setAttribute('data-step', String(n));
      var img = s.querySelector('img');
      if (img) img.src = arr[n];
      if (audioArmed) { ensureMediaAuto(idx, n); }
    }
    // ---- 动画音效内录：单音轨 + 每页连续播放窗口 [t0,t1]，翻到该页从 t0 连续播到 t1 ----
    // 跨步不中断、不被下一页切断；OSS 未配 CORS fetch 被拦时自动降级 <audio> 元素模式
    var AC = window.AudioContext || window.webkitAudioContext;
    var actx = null, abufs = {}, curSrc = null, audioArmed = false;
    var mediaMode = false, mediaEl = null, mediaTimer = null;
    function ensureAudio(){
      if (!AC) return;
      if (!actx) { try { actx = new AC(); } catch (e) { return; } }
      if (actx.state === 'suspended') { actx.resume(); }
      if (!audioArmed) {
        audioArmed = true;
        try { ensureMediaAuto(idx, frameIdx(slides[idx])); } catch (e) {}
      }
    }
    // ---- 统一音频引擎 ----
    // 媒体对象页(data-audiomedia)：PPT 样式播控 + 自动播放范围 [astart, aend]（帧级）——
    //   进入范围内的帧即从录音对应位置连续自动播，离开范围/翻页即停（与原 PPT 一致）；
    //   播控：喇叭(静音) + 播放/暂停 + 进度条；手动播放 = 原音频文件整段（不截断）。
    // 音效页(仅 data-audio/data-framet)：无控件，逐步跟帧播放（与原 PPT 一致）。
    var playing = null;      // {kind:'rec'|'file', i, from, until, __t0}（秒）
    var userPaused = false;  // 用户手动播控接管后抑制自动播放（翻页重置）
    var srcEls = {};         // 原音频文件 <audio>（手动整段播放，普通播放不受 CORS 限制）
    var uiTimer = null;
    var masterGain = null;

    function slideAudioInfo(s){
      if (!s) return null;
      var a = s.getAttribute('data-audio');
      var ft = [];
      try { ft = JSON.parse(s.getAttribute('data-framet') || '[]'); } catch (e) { ft = []; }
      var audios = [];
      try { audios = JSON.parse(s.getAttribute('data-audios') || '[]'); } catch (e2) { audios = []; }
      // 录音(data-audio)与音频对象(data-audios)至少有一个才算音频页；
      // 仅音频对象的页（无录制音轨）此前被误判为无音频 → 不建播控、不能播放
      if (!a && !audios.length) return null;
      return { url: a, ft: ft,
        t1: (parseFloat(s.getAttribute('data-t1') || '0') || 0) / 1000,
        astart: s.hasAttribute('data-astart') ? (parseFloat(s.getAttribute('data-astart')) || 0) / 1000 : null,
        aend: s.hasAttribute('data-aend') ? (parseFloat(s.getAttribute('data-aend')) || 0) / 1000 : null,
        audios: audios };
    }
    function loadAudio(i){
      var info = slideAudioInfo(slides[i]);
      if (!info || !info.url || abufs[i] || !actx) return;
      abufs[i] = { state: 'loading', buf: null };
      fetch(info.url).then(function (r) { return r.arrayBuffer(); })
        .then(function (ab) { return actx.decodeAudioData(ab); })
        .then(function (buf) { abufs[i] = { state: 'ready', buf: buf }; })
        .catch(function () { abufs[i] = { state: 'error', buf: null }; mediaMode = true; });
    }
    function ensureGain(){
      if (actx && !masterGain) { masterGain = actx.createGain(); masterGain.connect(actx.destination); }
    }
    function stopPlay(){
      if (curSrc) { try { curSrc.onended = null; curSrc.stop(); } catch (e) {} curSrc = null; }
      if (mediaTimer) { clearInterval(mediaTimer); mediaTimer = null; }
      if (mediaEl) { try { mediaEl.pause(); } catch (e) {} }
      if (playing && playing.kind === 'file' && srcEls[playing.i + '_' + (playing.k || 0)]) { try { srcEls[playing.i + '_' + (playing.k || 0)].pause(); } catch (e) {} }
      playing = null;
      refreshUi();
    }
    // 从录音音轨的 pos 播到 until（秒）；fetch 失败时降级用 <audio> 元素
    function startRecAt(i, pos, until){
      var info = slideAudioInfo(slides[i]); if (!info || !info.url) return;
      var rec = abufs[i];
      stopPlay();
      if (rec && rec.state === 'ready') {
        ensureGain();
        var src = actx.createBufferSource();
        src.buffer = rec.buf;
        src.connect(masterGain);
        try { src.start(0, pos, until - pos); } catch (e) { return; }
        curSrc = src;
        playing = { kind: 'rec', i: i, from: pos, until: until, __t0: actx.currentTime };
      } else if (rec && rec.state === 'error' && mediaMode) {
        if (!mediaEl) { mediaEl = new Audio(); mediaEl.preload = 'auto'; }
        if (mediaEl.__url !== info.url) { mediaEl.src = info.url; mediaEl.__url = info.url; }
        var tryPlay = function(){ try { mediaEl.currentTime = pos; mediaEl.play().catch(function(){}); } catch (e) {} };
        if (mediaEl.readyState >= 1) { tryPlay(); }
        else { mediaEl.addEventListener('loadedmetadata', tryPlay, { once: true }); }
        playing = { kind: 'rec', i: i, from: pos, until: until };
        mediaTimer = setInterval(function(){
          if (!mediaEl || !playing || playing.kind !== 'rec') { if (mediaTimer) { clearInterval(mediaTimer); mediaTimer = null; } return; }
          if (mediaEl.currentTime >= until || mediaEl.ended) { stopPlay(); }
        }, 100);
      }
      refreshUi();
    }
    function ensureSrcEl(i, k){
      var key = i + '_' + k;
      if (srcEls[key]) return srcEls[key];
      var info = slideAudioInfo(slides[i]);
      if (!info || !info.audios[k]) return null;
      var el = new Audio(); el.preload = 'auto'; el.src = info.audios[k].url;
      el.addEventListener('ended', function(){ playing = null; userPaused = false; refreshUi(); });
      srcEls[key] = el;
      return el;
    }
    // 手动播放原音频文件（整段，不截断）
    function startFile(i, k){
      var el = ensureSrcEl(i, k); if (!el) return;
      stopPlay();
      userPaused = true;
      try { el.currentTime = 0; } catch (e) {}
      el.play().catch(function(){});
      playing = { kind: 'file', i: i, k: k };
      refreshUi();
    }
    // 自动播放 = 与手动播放同一来源：原始音频文件整段（音频对象本身），不截断
    function startAutoFile(i){
      var el = ensureSrcEl(i, 0); if (!el) return;
      stopPlay();
      try { el.currentTime = 0; } catch (e) {}
      el.play().catch(function(){});
      playing = { kind: 'file', i: i, k: 0, auto: true };
      refreshUi();
    }
    function toggleFile(i, k){
      if (playing && playing.kind === 'file' && playing.i === i && playing.k === k) {
        var el = srcEls[i + '_' + k];
        if (el) { el.pause(); }
        playing = null; refreshUi();
      } else { startFile(i, k); }
    }
    // 统一调度：翻到第 i 页第 step 帧时该播什么
    function ensureMediaAuto(i, step){
      var s = slides[i]; if (!s) return;
      var info = slideAudioInfo(s);
      if (!info) { stopPlay(); return; }
      if (info.audios.length) {
        // 媒体对象页：进入"音频开始帧"即整段自动播放主音频，播完/翻页/离开前自动停
        if (userPaused) return;
        var ft = info.ft, k, s0 = 0;
        if (info.astart != null) {
          for (k = 0; k < ft.length; k++) { if (ft[k] <= info.astart * 1000) { s0 = k; } }
        }
        if (step >= s0) {
          if (playing && playing.i === i) { return; } // 连续不重启
          startAutoFile(i);
        } else if (playing && playing.i === i) {
          stopPlay();
        }
        return;
      }
      // 音效页：逐步跟帧（第 step 段 = [frameT[step], frameT[step+1])，末段到 data-t1）
      loadAudio(i);
      if (!info.ft.length) { stopPlay(); return; }
      var st = Math.max(0, Math.min(step || 0, info.ft.length - 1));
      var t0 = (info.ft[st] || 0) / 1000;
      var t1 = (st + 1 < info.ft.length) ? (info.ft[st + 1] || 0) / 1000 : info.t1;
      if (!(t1 > t0)) { return; }
      startRecAt(i, t0, t1);
    }
    // PPT 样式播控 UI：每个音频一个（小喇叭 + 播放/暂停 + 进度条，悬停显现）
    function buildUi(s, i){
      if (s.__pta) return;
      var info = slideAudioInfo(s);
      if (!info || !info.audios.length) return;
      s.__pta = [];
      info.audios.forEach(function(au, k){
        var root = document.createElement('div');
        root.className = 'pta';
        root.style.left = (au.abox ? au.abox.x : 10) + '%';
        root.style.top = (au.abox ? au.abox.y : 80) + '%';
        root.innerHTML = '<button class="pta-spk" title="静音">&#x1F50A;</button>' +
          '<div class="pta-row"><button class="pta-pp" title="播放/暂停">&#x25B6;</button>' +
          '<div class="pta-track"><i></i></div></div>';
        s.appendChild(root);
        s.__pta.push(root);
        root.querySelector('.pta-pp').addEventListener('click', function(e){ e.stopPropagation(); toggleFile(i, k); });
        root.querySelector('.pta-spk').addEventListener('click', function(e){
          e.stopPropagation();
          var muted = !(masterGain && masterGain.gain.value === 0);
          if (masterGain) { masterGain.gain.value = muted ? 0 : 1; }
          if (mediaEl) { mediaEl.muted = muted; }
          Object.keys(srcEls).forEach(function(key){ srcEls[key].muted = muted; });
          root.querySelector('.pta-spk').innerHTML = muted ? '&#x1F507;' : '&#x1F50A;';
        });
        root.querySelector('.pta-track').addEventListener('click', function(e){
          e.stopPropagation();
          var r = this.getBoundingClientRect();
          var f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
          if (playing && playing.kind === 'file' && playing.i === i && playing.k === k) {
            var el = srcEls[i + '_' + k];
            if (el && el.duration) { try { el.currentTime = f * el.duration; } catch (e2) {} }
          } else if (playing && playing.kind === 'rec' && slides[playing.i]) {
            var inf = slideAudioInfo(slides[playing.i]);
            if (inf && inf.astart != null && inf.aend > inf.astart) {
              startRecAt(playing.i, inf.astart + f * (inf.aend - inf.astart), inf.aend);
            }
          }
        });
      });
    }
    function refreshUi(){
      if (uiTimer) { clearInterval(uiTimer); uiTimer = null; }
      var s = slides[idx];
      var roots = (s && s.__pta) || [];
      if (!roots.length) { return; }
      var activeRoot = (playing && playing.kind === 'file') ? roots[playing.k || 0] : roots[0];
      var tick = function(){
        roots.forEach(function(root){
          var bar = root.querySelector('.pta-track i');
          var pp = root.querySelector('.pta-pp');
          if (!playing || root !== activeRoot) { bar.style.width = '0%'; pp.innerHTML = '&#x25B6;'; return; }
          pp.innerHTML = '&#x23F8;';
          var p = null;
          if (playing.kind === 'file') {
            var el = srcEls[playing.i + '_' + (playing.k || 0)];
            if (el && el.duration) { p = el.currentTime / el.duration; }
          } else if (mediaMode && mediaEl && playing.kind === 'rec') {
            var inf = slideAudioInfo(slides[playing.i]);
            if (inf && inf.astart != null && inf.aend > inf.astart) { p = (mediaEl.currentTime - inf.astart) / (inf.aend - inf.astart); }
          } else if (actx && playing.__t0 != null) {
            var inf2 = slideAudioInfo(slides[playing.i]);
            var pos2 = (playing.from || 0) + (actx.currentTime - playing.__t0);
            if (inf2 && inf2.astart != null && inf2.aend > inf2.astart) { p = (pos2 - inf2.astart) / (inf2.aend - inf2.astart); }
            else { var span = (playing.until || 0) - (playing.from || 0); if (span > 0) { p = (pos2 - playing.from) / span; } }
          }
          if (p != null) { bar.style.width = Math.max(0, Math.min(1, p)) * 100 + '%'; }
        });
      };
      tick();
      uiTimer = setInterval(tick, 200);
    }
    function updateInfo(){
      if (!info) return;
      var s = slides[idx];
      var fc = frameCount(s);
      if (fc > 1) info.textContent = (idx + 1) + ' / ' + slides.length + ' · 动画步骤 ' + frameIdx(s) + ' / ' + (fc - 1);
      else info.textContent = (idx + 1) + ' / ' + slides.length;
    }
    function show(i){
      idx = (i + slides.length) % slides.length;
      slides.forEach(function(s, n){ s.classList.toggle('active', n === idx); });
      updateInfo();
      userPaused = false;
      stopPlay();
      loadAudio(idx);
      var cs = slides[idx];
      if (cs && cs.getAttribute('data-audios')) { buildUi(cs, idx); }
      if (audioArmed) { ensureMediaAuto(idx, frameIdx(cs)); }
    }
    function next(){
      var s = slides[idx];
      if (frameCount(s) > 1 && frameIdx(s) < frameCount(s) - 1) { setFrame(s, frameIdx(s) + 1); updateInfo(); return; }
      show(idx + 1);
    }
    function prev(){
      var s = slides[idx];
      if (frameCount(s) > 1 && frameIdx(s) > 0) { setFrame(s, frameIdx(s) - 1); updateInfo(); return; }
      show(idx - 1);
    }
    document.addEventListener('keydown', function(e){
      ensureAudio();
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'NumpadEnter') { next(); e.preventDefault(); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { prev(); e.preventDefault(); }
    });
    var nb = document.getElementById('nextBtn');
    var pb = document.getElementById('prevBtn');
    if (nb) nb.addEventListener('click', next);
    if (pb) pb.addEventListener('click', prev);
    // 点击画面翻下一页/下一帧：兼容模拟鼠标点击的翻页笔（导航按钮与视频除外，避免双重触发/影响播控）
    document.addEventListener('click', function(e){
      ensureAudio();
      var t = e.target;
      while (t) {
        if (t.id === 'nav' || t.tagName === 'VIDEO') { return; }
        t = t.parentElement;
      }
      next();
    });
    var fb = document.getElementById('fullBtn');
    if (fb) fb.addEventListener('click', function(){
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen();
    });
    var tx = 0;
    document.addEventListener('touchstart', function(e){ tx = e.touches[0].clientX; }, {passive:true});
    document.addEventListener('touchend', function(e){
      var dx = e.changedTouches[0].clientX - tx;
      if (Math.abs(dx) > 50) { dx > 0 ? prev() : next(); }
    }, {passive:true});
    ${copyJs}
    // 预建所有音频媒体页的 PPT 样式播控
    slides.forEach(function(s, n){ if (s.getAttribute && s.getAttribute('data-audios')) { buildUi(s, n); } });
    show(0);
  })();
  `

  // 受保护导出引导脚本（纯 ES5）：读资源清单 → 登录鉴权换签名 URL → 回填占位并注入播放器；
  // 页内资源全部是无效的 pptasset:// 钥匙，离开 eOnline 平台拿不到签名，无法播放/下载
  const bootstrapJs = `
(function(){
  var manifestEl = document.getElementById('ppt-manifest');
  var keys = [];
  try { keys = JSON.parse(manifestEl ? manifestEl.textContent : '[]'); } catch (e) {}
  var mm = location.pathname.match(/\\/files\\/ppt\\/stream\\/(\\d+)/);
  var fileId = mm ? mm[1] : null;
  var apiBase = mm ? location.pathname.substring(0, mm.index) : '';
  var cm = document.cookie.match(/(?:^|;\\s*)Admin-Token=([^;]+)/);
  var token = cm ? decodeURIComponent(cm[1]) : null;
  var guard = document.getElementById('ppt-guard');
  var keyMap = null;
  var resignCount = 0;
  function fail(msg){
    if (guard) {
      guard.style.display = 'flex';
      var g = document.getElementById('ppt-guard-msg');
      if (g && msg) { g.textContent = msg; }
    }
  }
  function resolveUrl(u){
    if (!u) { return u; }
    u = String(u);
    if (u.indexOf('pptasset://') !== 0) { return u; }
    var k = u.substring('pptasset://'.length);
    return (keyMap && keyMap[k]) ? keyMap[k] : u;
  }
  function resignOne(key, done){
    fetch(apiBase + '/files/ppt/sign', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }, body: JSON.stringify({ fileId: Number(fileId), keys: [key] }) })
      .then(function(r){ return r.json(); })
      .then(function(res){ if (res && res.code === 200 && res.data && res.data[key]) { done(res.data[key]); } })
      .catch(function(){});
  }
  function hookMedia(el){
    if (el.__pptHooked) { return; }
    el.__pptHooked = true;
    el.addEventListener('error', function(){
      var key = el.__pptKey;
      if (!key || resignCount >= 3) { return; }
      var pos = 0;
      try { pos = el.currentTime || 0; } catch (e) {}
      resignCount++;
      resignOne(key, function(url){
        el.src = url;
        el.load();
        if (pos > 0) {
          var restore = function(){ try { el.currentTime = pos; } catch (e) {} el.removeEventListener('loadedmetadata', restore); };
          el.addEventListener('loadedmetadata', restore);
        }
      });
    });
  }
  function rewrite(){
    var slides = document.querySelectorAll('.slide');
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      var img = s.querySelector('img');
      if (img) { var ds = img.getAttribute('data-src'); if (ds) { img.src = resolveUrl(ds); } }
      var df = s.getAttribute('data-frames');
      if (df) { try { var arr = JSON.parse(df); for (var j = 0; j < arr.length; j++) { arr[j] = resolveUrl(arr[j]); } s.setAttribute('data-frames', JSON.stringify(arr)); } catch (e1) {} }
      var da = s.getAttribute('data-audio');
      if (da) { s.setAttribute('data-audio', resolveUrl(da)); }
      var das = s.getAttribute('data-audios');
      if (das) { try { var al = JSON.parse(das); for (var k2 = 0; k2 < al.length; k2++) { al[k2].url = resolveUrl(al[k2].url); } s.setAttribute('data-audios', JSON.stringify(al)); } catch (e2) {} }
      var vids = s.querySelectorAll('video');
      for (var v = 0; v < vids.length; v++) {
        var vds = vids[v].getAttribute('data-src');
        if (vds) { vids[v].__pptKey = vds.indexOf('pptasset://') === 0 ? vds.substring('pptasset://'.length) : null; vids[v].src = resolveUrl(vds); }
        var vpo = vids[v].getAttribute('data-poster');
        if (vpo) { vids[v].poster = resolveUrl(vpo); }
        hookMedia(vids[v]);
      }
    }
  }
  function startPlayer(){
    var src = document.getElementById('ppt-player-src');
    if (!src) { return; }
    var ns = document.createElement('script');
    ns.textContent = src.textContent;
    document.body.appendChild(ns);
  }
  if (!fileId) { fail('请在 eOnline 平台内打开本课件'); return; }
  if (!token) { fail('请登录 eOnline 平台后使用'); return; }
  if (!keys.length) { fail('课件资源清单为空'); return; }
  fetch(apiBase + '/files/ppt/sign', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }, body: JSON.stringify({ fileId: Number(fileId), keys: keys }) })
    .then(function(r){ return r.json(); })
    .then(function(res){
      if (!res || res.code !== 200 || !res.data) { fail(res && res.msg ? res.msg : '资源鉴权失败，请重新登录'); return; }
      keyMap = res.data;
      rewrite();
      startPlayer();
    })
    .catch(function(){ fail('资源加载失败，请检查登录状态'); });
})();
`

  // 受保护导出附加片段（protected=false 时均为空串/原样 script，输出与旧版逐字节一致）
  const manifestHtml = isProtected
    ? `<script id="ppt-manifest" type="application/json">${JSON.stringify(assetKeys).replace(/<\//g, '<\\/')}</script>\n  `
    : ''
  const guardHtml = isProtected
    ? `<div id="ppt-guard" style="display:none;position:fixed;inset:0;z-index:99;background:#1a1a2e;color:#fff;align-items:center;justify-content:center;font-family:sans-serif;"><div style="text-align:center;"><div id="ppt-guard-msg" style="font-size:22px;margin-bottom:12px;">请登录 eOnline 平台后使用</div><div style="color:#8a8aa3;font-size:14px;">课件受保护，需在 eOnline 平台内登录授权后播放</div></div></div>\n  `
    : ''
  const playerScriptHtml = isProtected
    ? `<script id="ppt-player-src" type="text/plain">${js}</script>\n  <script>${bootstrapJs}</script>`
    : `<script>${js}</script>`

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
<meta name="robots" content="noindex">
<title>${rasterEscapeHtml(title)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { height: 100%; }
  body { overflow: hidden; background: #141824; font-family: "Microsoft YaHei", sans-serif; -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
  #stage { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; }
  .slide { position: absolute; display: none; }
  .slide.active { display: block; animation: fadeIn .35s ease; }
  .slide img { display: block; width: min(96vw, calc(92vh * ${pageW} / ${pageH})); aspect-ratio: ${pageW} / ${pageH}; box-shadow: 0 20px 60px rgba(0,0,0,.45); background: #fff; }
  /* PPT 样式音频播控：小喇叭在上，播放/暂停 + 进度条在下方，悬停显现 */
  .pta { position: absolute; z-index: 8; display: flex; flex-direction: column; align-items: flex-start; gap: 5px; background: rgba(18,20,30,.78); border-radius: 9px; padding: 6px 8px; opacity: .3; transition: opacity .15s ease; }
  .slide:hover .pta { opacity: 1; }
  .pta-row { display: flex; align-items: center; gap: 6px; }
  .pta button { width: 22px; height: 22px; border: none; border-radius: 50%; background: rgba(255,255,255,.16); color: #fff; font-size: 11px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
  .pta button:hover { background: rgba(255,255,255,.32); }
  .pta .pta-track { position: relative; width: 130px; height: 5px; background: rgba(255,255,255,.22); border-radius: 3px; cursor: pointer; }
  .pta .pta-track i { position: absolute; left: 0; top: 0; bottom: 0; width: 0; background: #4fc3f7; border-radius: 3px; }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  #nav { position: fixed; left: 50%; bottom: 18px; transform: translateX(-50%); display: flex; align-items: center; gap: 14px; z-index: 20; }
  #nav button { width: 46px; height: 46px; border-radius: 50%; border: none; cursor: pointer; background: rgba(255,255,255,.14); color: #fff; font-size: 20px; line-height: 1; }
  #nav button:hover { background: rgba(255,255,255,.28); }
  #pageInfo { color: rgba(255,255,255,.8); font-size: 14px; min-width: 70px; text-align: center; }
  #wm { position: fixed; inset: 0; pointer-events: none; z-index: 30; overflow: hidden; color: #fff; }
</style>
</head>
<body>
  ${manifestHtml}<div id="stage">
    ${slides}
  </div>
  ${navHtml}
  <div id="wm">${wmHtml}</div>
  ${guardHtml}${playerScriptHtml}
</body>
</html>`
}

/**
 * 栅格化预览：把 pages（[{id,name,img,type,frames,videoUrl,videoBox}]）渲染进现有容器，返回控制器
 * @param {HTMLElement} container 目标容器（内部会被清空）
 * @param {Array} pages  [{id,name,img,type,frames,videoUrl,videoBox}]
 * @param {object} options { watermark, onChange, pageW, pageH }
 * @returns {{show,next,prev,total}}
 */
export function renderRasterInto(container, pages, options = {}) {
  const pageW = Number(options.pageW) || 960
  const pageH = Number(options.pageH) || 540
  const slides = (pages || []).map((p) => {
    const d = document.createElement('div')
    d.className = 'slide'
    const img = document.createElement('img')
    img.alt = p.name || ''
    img.draggable = false
    d.appendChild(img)
    // sequence：动画关键帧序列（点击「下一页」逐帧播放）
    if (p.type === 'sequence' && p.frames && p.frames.length >= 2) {
      d.dataset.frames = JSON.stringify(p.frames)
      d.dataset.step = '0'
      img.src = p.frames[0]
    } else {
      img.src = p.img || ''
    }
    // 视频对象：全量数组（兼容旧单字段）；整页视频(type=video)为用户补录
    const videoList = Array.isArray(p.videos) && p.videos.length
      ? p.videos
      : (p.videoUrl ? [{ url: p.videoUrl, box: p.videoBox }] : [])
    videoList.forEach((v) => {
      if (!v.url) return
      const vv = document.createElement('video')
      vv.src = v.url
      vv.controls = true
      vv.preload = 'metadata'
      if (v.box && Number(v.box.w) > 0) {
        vv.style.cssText = 'position:absolute;left:' + ((Number(v.box.x) || 0) / pageW * 100) + '%;top:' + ((Number(v.box.y) || 0) / pageH * 100) + '%;width:' + ((Number(v.box.w) || 100) / pageW * 100) + '%;height:' + ((Number(v.box.h) || 100) / pageH * 100) + '%;object-fit:contain;background:#000;border-radius:2px;'
      } else {
        vv.poster = p.img || ''
        vv.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;object-fit:contain;background:#000;'
      }
      d.appendChild(vv)
    })
    // 音频媒体对象：全量数组（兼容旧单字段），整段文件 + 原生播控
    const audioList = Array.isArray(p.audios) && p.audios.length
      ? p.audios
      : (p.audioMedia ? [{ url: p.audioMedia, box: p.audioBox }] : [])
    audioList.forEach((au) => {
      if (!au.url) return
      const b = (au.box && Number(au.box.w) > 0) ? au.box : null
      const ax = b ? (Number(b.x) || 0) / pageW * 100 : 10
      const ay = b ? (Number(b.y) || 0) / pageH * 100 : 80
      const aw = b ? (Number(b.w) || 100) / pageW * 100 : 80
      const a = document.createElement('audio')
      a.src = au.url
      a.controls = true
      a.preload = 'metadata'
      a.style.cssText = 'position:absolute;left:' + ax + '%;top:' + ay + '%;width:' + aw + '%;'
      d.appendChild(a)
    })
    return d
  })
  container.innerHTML = ''
  // 仅当容器未定位时才补 relative：容器本身已是 absolute/relative 时强行覆盖会把
  // 依赖其尺寸的布局（如 inset:0 撑满舞台）塌成 0 高，图片随之不显示
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative'
  }
  slides.forEach((s) => container.appendChild(s))

  let infoEl = null
  if (options.showInfo !== false) {
    infoEl = document.createElement('div')
    infoEl.style.cssText = 'position:absolute;right:14px;bottom:10px;color:#fff;font-size:13px;background:rgba(0,0,0,.45);padding:2px 10px;border-radius:10px;z-index:5;'
    container.appendChild(infoEl)
  }
  return rasterNavCore(slides, infoEl, options)
}
